// Utility function to find a folder by ID in the folder structure
export const findFolderById = (folderStructure, folderId) => {
  if (!folderStructure) return null;
  
  const searchFolder = (folder) => {
    if (folder.Id === folderId) {
      return folder;
    }
    
    if (folder.Children) {
      for (const child of folder.Children) {
        const result = searchFolder(child);
        if (result) return result;
      }
    }
    
    return null;
  };
  
  return searchFolder(folderStructure);
};

// Utility function to find the path to a folder (for breadcrumb/expansion)
export const findFolderPath = (folderStructure, targetFolderId) => {
  if (!folderStructure) return [];
  
  const findPath = (folder, path = []) => {
    const currentPath = [...path, folder.Id];
    
    if (folder.Id === targetFolderId) {
      return currentPath;
    }
    
    if (folder.Children) {
      for (const child of folder.Children) {
        const result = findPath(child, currentPath);
        if (result) return result;
      }
    }
    
    return null;
  };
  
  return findPath(folderStructure) || [];
};

// Utility function to flatten folder structure and create lookup maps
export const createFolderLookupMaps = (folderStructure) => {
  const folderMap = new Map(); // folderId -> folder object
  const folderFilesMap = new Map(); // folderId -> promise of files
  const folderPathMap = new Map(); // folder path/url -> folder object
  
  const flattenFolders = (folder, parentPath = []) => {
    const currentPath = [...parentPath, folder.Id];
    
    // Store folder in maps
    folderMap.set(folder.Id, folder);
    if (folder.FileUrl) {
      folderPathMap.set(folder.FileUrl, folder);
    }
    if (folder.ServerRelativeUrl) {
      folderPathMap.set(folder.ServerRelativeUrl, folder);
    }
    
    // Store folder path for expansion
    folder._path = currentPath;
    
    // Recursively process children
    if (folder.Children) {
      folder.Children.forEach(child => {
        flattenFolders(child, currentPath);
      });
    }
  };
  
  if (folderStructure) {
    flattenFolders(folderStructure);
  }
  
  return { folderMap, folderFilesMap, folderPathMap };
};

// Utility function to find which folder contains a specific file (client-side only)
export const findFolderContainingFile = async (folderStructure, fileId, lookupMaps = null) => {
  if (!folderStructure || !fileId) return null;
  
  // Create lookup maps if not provided
  if (!lookupMaps) {
    lookupMaps = createFolderLookupMaps(folderStructure);
  }
  
  const { folderMap, folderFilesMap } = lookupMaps;
  
  // Function to check a specific folder for the file
  const checkFolderForFile = async (folderId) => {
    const folder = folderMap.get(folderId);
    if (!folder || !folder.FileUrl) return null;
    
    try {
      // Check if we already have files for this folder
      let filesPromise = folderFilesMap.get(folderId);
      
      if (!filesPromise) {
        // Create and cache the promise
        filesPromise = fetch(`${process.env.REACT_APP_SHAREPOINT_FILE_FUNCTION_URL}?folderUrl=${encodeURIComponent(folder.FileUrl)}`)
          .then(response => response.ok ? response.json() : [])
          .catch(error => {
            console.error(`Error fetching files from folder ${folder.Name}:`, error);
            return [];
          });
        
        folderFilesMap.set(folderId, filesPromise);
      }
      
      const files = await filesPromise;
      const foundFile = files.find(file => file.Id === fileId);
      
      if (foundFile) {
        return { folder, file: foundFile };
      }
    } catch (error) {
      console.error(`Error checking folder ${folder.Name}:`, error);
    }
    
    return null;
  };
  
  // Strategy 1: Check folders that are likely to contain files (leaf folders first)
  const allFolders = Array.from(folderMap.values());
  
  // Sort folders by likelihood of containing files (leaf folders first, then by depth)
  const sortedFolders = allFolders
    .filter(folder => folder.FileUrl) // Only folders that can contain files
    .sort((a, b) => {
      // Prioritize leaf folders (folders with no children or few children)
      const aChildCount = a.Children ? a.Children.length : 0;
      const bChildCount = b.Children ? b.Children.length : 0;
      
      if (aChildCount === 0 && bChildCount > 0) return -1;
      if (bChildCount === 0 && aChildCount > 0) return 1;
      
      // Then prioritize by depth (deeper folders first as they're more likely to have files)
      const aDepth = a._path ? a._path.length : 0;
      const bDepth = b._path ? b._path.length : 0;
      
      return bDepth - aDepth;
    });
  
  // Strategy 2: Check folders in batches to avoid overwhelming the server
  const batchSize = 5; // Check 5 folders at a time
  
  for (let i = 0; i < sortedFolders.length; i += batchSize) {
    const batch = sortedFolders.slice(i, i + batchSize);
    
    // Check folders in parallel within the batch
    const batchPromises = batch.map(folder => checkFolderForFile(folder.Id));
    const results = await Promise.allSettled(batchPromises);
    
    // Check if any folder in this batch contains the file
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        return result.value;
      }
    }
  }
  
  return null;
};

// Helper function to find folder by path (client-side)
const findFolderByPath = (folderStructure, targetPath, lookupMaps = null) => {
  if (!folderStructure || !targetPath) return null;
  
  if (!lookupMaps) {
    lookupMaps = createFolderLookupMaps(folderStructure);
  }
  
  const { folderPathMap } = lookupMaps;
  
  // Direct path match
  let folder = folderPathMap.get(targetPath);
  if (folder) return folder;
  
  // Try to find partial matches
  for (const [path, folderObj] of folderPathMap.entries()) {
    if (targetPath.includes(path) || path.includes(targetPath)) {
      return folderObj;
    }
  }
  
  return null;
};

// Utility function to find a file by ID across all folders
export const findFileById = async (fileId) => {
  try {
    // Call your search function or a specific API endpoint to find the file
    const response = await fetch(`${process.env.REACT_APP_SHAREPOINT_SEARCH_FUNCTION_URL}?fileId=${fileId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error finding file:', error);
    return null;
  }
};